import React, { useMemo, useEffect } from 'react'
import { Link, useNavigate, useLocation } from 'react-router-dom'
import { useForm, Controller } from 'react-hook-form'
import { useAuth } from '../contexts/AuthContext'
import { Layout, Input } from '../../../shared/components'
import { formatDocumentOrEmail } from '../../../shared/utils'
import { User } from '../../../types/api'

interface LoginFormData {
  emailOrCpf: string
  password: string
}

const Login: React.FC = () => {
  const { login, user, isLoading: authLoading, error: authError, isAuthenticated } = useAuth()
  const navigate = useNavigate()
  const location = useLocation()

  const {
    register,
    handleSubmit,
    control,
    formState: { errors }
  } = useForm<LoginFormData>({
    defaultValues: {
      emailOrCpf: '',
      password: ''
    }
  })

  // Pegar a rota de redirecionamento após login
  // Prioridade: 1. Query param 'redirect' 2. location.state.from 3. '/conta'
  const redirectTo = useMemo(() => {
    const searchParams = new URLSearchParams(location.search)
    const redirectParam = searchParams.get('redirect')
    
    if (redirectParam) {
      // Validar que o redirect é uma rota interna
      if (redirectParam.startsWith('/')) {
        return redirectParam
      }
    }
    
    return location.state?.from?.pathname || '/conta'
  }, [location.search, location.state])

  const getDefaultRedirectPath = (user?: User | null) => {
    if (!user?.role) {
      return '/conta'
    }

    const adminRoles: Array<User['role']> = ['ADMIN', 'MANAGER', 'OPERATOR']
    return adminRoles.includes(user.role) ? '/admin' : '/conta'
  }

  // Redirecionar quando login for bem-sucedido
  useEffect(() => {
    if (isAuthenticated && user) {
      const fallbackRedirect = getDefaultRedirectPath(user)
      const destination = redirectTo === '/conta' ? fallbackRedirect : redirectTo
      navigate(destination, { replace: true })
    }
  }, [isAuthenticated, user, navigate, redirectTo])

  const onSubmit = async (data: LoginFormData) => {
    try {
      await login(data.emailOrCpf, data.password)
      // O redirecionamento será tratado pelo useEffect quando o estado mudar
    } catch (error) {
      // Erro será exibido pelo authError do Redux
    }
  }

  const displayError = authError

  return (
    <Layout>
      <div className="min-h-[calc(100vh-200px)] flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8 bg-gray-50">
        <div className="max-w-5xl w-full bg-white rounded-2xl shadow-xl overflow-hidden flex flex-col md:flex-row">
          
          {/* Lado Esquerdo - Imagem Decorativa */}
          <div className="w-full md:w-1/2 bg-blue-600 relative hidden md:block">
            <img 
              src="https://images.unsplash.com/photo-1486312338219-ce68d2c6f44d?q=80&w=2072&auto=format&fit=crop" 
              alt="BaterPower Login" 
              className="absolute inset-0 w-full h-full object-cover mix-blend-overlay opacity-20"
            />
            <div className="absolute inset-0 bg-gradient-to-br from-blue-600/90 to-blue-900/90"></div>
            
            <div className="relative z-10 flex flex-col items-center justify-center h-full text-white px-12 text-center p-12">
              <div className="mb-8 p-4 bg-white/10 rounded-full backdrop-blur-sm">
                <svg className="w-12 h-12 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
              </div>
              <h2 className="text-3xl font-bold mb-6 font-display">Energia que move você</h2>
              <p className="text-blue-100 text-lg leading-relaxed">
                Acesse sua plataforma exclusiva para gerenciar pedidos, acompanhar entregas e aproveitar as melhores ofertas em baterias.
              </p>
            </div>
          </div>

          {/* Lado Direito - Formulário */}
          <div className="w-full md:w-1/2 p-8 md:p-12 lg:p-16 flex flex-col justify-center">
            <div className="text-center md:text-left mb-10">
              <h2 className="text-3xl font-bold text-gray-900 font-display">Bem-vindo de volta</h2>
              <p className="mt-3 text-base text-gray-600">Por favor, insira seus dados para entrar.</p>
            </div>

            {/* Redirect Info */}
            {redirectTo !== '/conta' && (
              <div className="mb-6 bg-blue-50 border border-blue-200 text-blue-700 px-4 py-3 rounded-xl flex items-center shadow-sm">
                <svg className="w-5 h-5 mr-3 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <span className="text-sm font-medium">
                  Faça login para continuar com {redirectTo === '/checkout' ? 'seu pedido' : 'a página solicitada'}
                </span>
              </div>
            )}

            {/* Error Message */}
            {displayError && (
              <div className="mb-6 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl flex items-center shadow-sm">
                <svg className="w-5 h-5 mr-3 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <span className="text-sm font-medium">{displayError}</span>
              </div>
            )}

            <form className="space-y-6" onSubmit={handleSubmit(onSubmit)}>
              <div className="w-full">
                <Controller
                  name="emailOrCpf"
                  control={control}
                  rules={{
                    required: 'Email, CPF ou CNPJ é obrigatório'
                  }}
                  render={({ field }) => (
                    <Input
                      id="emailOrCpf"
                      label="Email, CPF ou CNPJ"
                      type="text"
                      {...field}
                      onChange={(e) => {
                        const formatted = formatDocumentOrEmail(e.target.value)
                        field.onChange(formatted)
                      }}
                      placeholder="Digite seu email, CPF ou CNPJ"
                      maxLength={100}
                      error={!!errors.emailOrCpf}
                      helperText={errors.emailOrCpf?.message}
                      leftIcon={
                        <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                        </svg>
                      }
                    />
                  )}
                />
              </div>

              <div className="w-full">
                <Input
                  id="password"
                  label="Senha"
                  type="password"
                  {...register('password', {
                    required: 'Senha é obrigatória'
                  })}
                  placeholder="Digite sua senha"
                  error={!!errors.password}
                  helperText={errors.password?.message}
                  leftIcon={
                    <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                    </svg>
                  }
                />
              </div>

              <div className="flex items-center justify-end">
                <div className="text-sm">
                  <Link to="/esqueci-senha" className="font-medium text-blue-600 hover:text-blue-500 transition-colors">
                    Esqueceu sua senha?
                  </Link>
                </div>
              </div>

              <button
                type="submit"
                disabled={authLoading}
                className="w-full flex justify-center py-3.5 px-4 border border-transparent rounded-xl shadow-lg shadow-blue-500/30 text-sm font-semibold text-white bg-blue-600 hover:bg-blue-700 hover:shadow-blue-600/40 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed disabled:shadow-none transition-all transform hover:-translate-y-0.5"
              >
                {authLoading ? (
                  <div className="flex items-center">
                    <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Entrando...
                  </div>
                ) : (
                  'Entrar'
                )}
              </button>

              <div className="mt-8 pt-6 border-t border-gray-100 text-center">
                <p className="text-sm text-gray-600">
                  Ainda não tem uma conta?{' '}
                  <Link to="/cadastro" className="font-semibold text-blue-600 hover:text-blue-700 hover:underline transition-colors">
                    Criar conta agora
                  </Link>
                </p>
              </div>
            </form>
          </div>
        </div>
      </div>
    </Layout>
  )
}

export default Login
